import { Flame, Snowflake } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { productsApi } from '@/api/endpoints/products'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Whether the guest is asked warm or cold, set from the row instead of from the product screen
 * (CHG-2026-09-menu-editor-redesign).
 *
 * The two halves are not two settings — the control pictures what the guest gets, in the order
 * app's own colours (`--brand-primary` / `--brand-info`), so the manager recognises it. One click
 * flips the single boolean behind it. A four-state model was considered and rejected: see the
 * change's design.md.
 */
export function TemperatureToggle({
  sku,
  value,
  menuId,
  canEdit,
  className,
}: {
  sku: string
  value: boolean
  menuId: number
  canEdit: boolean
  className?: string
}) {
  const { t } = useTranslation('menus')
  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: (next: boolean) => productsApi.setTemperatureChoice(sku, next),
    onSuccess: () => {
      toast.success(t('itemRow.temp.saved'))
      queryClient.invalidateQueries({ queryKey: ['menus', menuId, 'contents'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: () => toast.error(t('itemRow.temp.saveFailed')),
  })

  const half = 'flex h-[30px] w-[31px] items-center justify-center'
  const on = value
  const label = `${on ? t('itemRow.temp.on') : t('itemRow.temp.off')} — ${t('itemRow.temp.scope')}`

  const body = (
    <span
      className={cn(
        'inline-flex overflow-hidden rounded-lg border',
        on ? 'border-transparent' : 'border-input',
      )}
    >
      <span
        className={cn(
          half,
          on
            ? 'bg-[#d54243] text-white'
            : 'bg-background text-muted-foreground/50',
        )}
      >
        <Flame className="h-[15px] w-[15px]" />
      </span>
      <span
        className={cn(
          half,
          on
            ? 'bg-[#17428c] text-white'
            : 'border-l border-input bg-background text-muted-foreground/50',
        )}
      >
        <Snowflake className="h-[15px] w-[15px]" />
      </span>
    </span>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {canEdit ? (
          <button
            type="button"
            aria-pressed={on}
            aria-label={label}
            disabled={save.isPending}
            onClick={() => save.mutate(!value)}
            className={cn(
              'items-center justify-center justify-self-center disabled:opacity-60',
              className,
            )}
          >
            {body}
          </button>
        ) : (
          // Without the permission this is a badge, not a disabled input: a greyed-out control
          // invites a click that will never work.
          <span
            aria-label={label}
            className={cn(
              'items-center justify-center justify-self-center',
              className,
            )}
          >
            {body}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{label}</TooltipContent>
    </Tooltip>
  )
}
